import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { SEOConfig } from '../../types';

interface SEOContextType {
    seoConfig: SEOConfig;
    updateSEO: (config: SEOConfig) => void;
}

const SEOContext = createContext<SEOContextType | null>(null);

interface SEOProviderProps {
    children: ReactNode;
    initialSEO?: SEOConfig;
}

export function SEOProvider({ children, initialSEO }: SEOProviderProps) {
    const [seoConfig, setSeoConfig] = useState<SEOConfig>(initialSEO || {});

    const updateSEO = (config: SEOConfig) => {
        setSeoConfig(prev => ({ ...prev, ...config }));
    };

    useEffect(() => {
        // Actualizar meta tags en el DOM
        updateMetaTags(seoConfig);
    }, [seoConfig]);

    return (
        <SEOContext.Provider value={{ seoConfig, updateSEO }}>
            {children}
        </SEOContext.Provider>
    );
}

export function useSEOContext(): SEOContextType {
    const context = useContext(SEOContext);
    if (!context) {
        throw new Error('useSEOContext must be used within a SEOProvider');
    }
    return context;
}

function updateMetaTags(config: SEOConfig) {
    // Actualizar title
    if (config.title) {
        document.title = config.title;
    }

    // Actualizar meta description
    updateMetaTag('description', config.description);

    // Actualizar Open Graph tags
    updateMetaTag('og:title', config.ogTitle || config.title);
    updateMetaTag('og:description', config.ogDescription || config.description);
    updateMetaTag('og:image', config.ogImage);

    // Actualizar Twitter Card tags
    updateMetaTag('twitter:card', config.twitterCard || 'summary_large_image');
    updateMetaTag('twitter:title', config.ogTitle || config.title);
    updateMetaTag('twitter:description', config.ogDescription || config.description);
    updateMetaTag('twitter:image', config.ogImage);

    // Actualizar keywords
    if (config.keywords && config.keywords.length > 0) {
        updateMetaTag('keywords', config.keywords.join(', '));
    }

    // Actualizar canonical URL
    if (config.canonical) {
        updateLinkTag('canonical', config.canonical);
    }
}

function updateMetaTag(name: string, content?: string) {
    if (!content) return;

    let selector = `meta[name="${name}"]`;
    if (name.startsWith('og:') || name.startsWith('twitter:')) {
        selector = `meta[property="${name}"]`;
    }

    let meta = document.querySelector(selector) as HTMLMetaElement;

    if (!meta) {
        meta = document.createElement('meta');
        if (name.startsWith('og:') || name.startsWith('twitter:')) {
            meta.setAttribute('property', name);
        } else {
            meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
    }

    meta.setAttribute('content', content);
}

function updateLinkTag(rel: string, href: string) {
    let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;

    if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
    }

    link.setAttribute('href', href);
}
